import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import {
  appApi,
  useGetNcsTeamQuery,
  useGetAllStaffQuery,
} from "../../../../store/appApi";
import { panelClass, fieldClass, btnPrimary, btnGhost, fmtDate } from "./config";
import { uiConfirm, uiPrompt, uiToast } from "./ui";
import {
  addTeamMemberApi,
  removeTeamMemberApi,
  updateTeamMemberApi,
} from "../../../../services/netplusCustomerStatus";

const AdminOnly = () => (
  <div className={`${panelClass} p-10 text-center`}>
    <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
    <p className="mt-2 text-sm text-gray-500">
      The Netplus team roster is managed by a super-admin.
    </p>
  </div>
);

// Turn an axios error into something the user can act on, instead of a bare
// "Failed" — covers a JSON { message }, a status-only response, and the
// no-response (network / CORS / wrong API host) case.
const errText = (e) => {
  if (e?.response?.data?.message) return e.response.data.message;
  if (e?.response) return `Server error (HTTP ${e.response.status}).`;
  return `Could not reach the server — ${e?.message || "network error"}.`;
};

const NcsTeam = () => {
  const dispatch = useDispatch();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [q, setQ] = useState("");
  const { data: teamData, isFetching } = useGetNcsTeamQuery({ all: true });
  // Pull the whole staff directory (not just the active project's members) so
  // any employee can be added to the Netplus team / assignee list.
  const { data: staffData } = useGetAllStaffQuery(
    { limit: 500, allProjects: true },
    { skip: !isSuperAdmin },
  );

  const members = teamData?.members || [];
  const activeMembers = members.filter((m) => m.active);
  const activeIds = new Set(
    activeMembers.map((m) => String(m.userId?._id || m.userId)),
  );
  const staff = staffData?.staff || staffData?.users || staffData?.data || [];
  const candidates = staff
    .filter((s) => !activeIds.has(String(s._id)))
    .filter((s) =>
      q.trim()
        ? `${s.name} ${s.phone} ${s.role}`.toLowerCase().includes(q.trim().toLowerCase())
        : true,
    );

  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));

  const add = async (s) => {
    try {
      await addTeamMemberApi(s._id, s.role || "");
      refresh();
      uiToast({ type: "success", title: `${s.name || "Member"} added to the team` });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't add member", message: errText(e) });
    }
  };

  const remove = async (m) => {
    const ok = await uiConfirm({
      title: `Remove ${m.name || "member"}?`,
      message: "Their Netplus Customer Status access will be revoked.",
      confirmText: "Remove",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await removeTeamMemberApi(m._id);
      refresh();
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't remove", message: errText(e) });
    }
  };

  const editRole = async (m) => {
    const value = await uiPrompt({
      title: "Role label",
      label: "Role",
      initial: m.role || "",
      placeholder: "e.g. Telecaller",
    });
    if (value === null) return;
    try {
      await updateTeamMemberApi(m._id, { role: value || "" });
      refresh();
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't update role", message: errText(e) });
    }
  };

  if (!isSuperAdmin) {
    return (
      <>
        <PageTitle activeMenu="Team" motherMenu="Netplus" />
        <AdminOnly />
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Team" motherMenu="Netplus" />
      <div className="grid gap-6 lg:grid-cols-2">
        <section className={panelClass}>
          <div className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Netplus team ({activeMembers.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {isFetching && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
            )}
            {activeMembers.map((m) => (
                <div key={m._id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <div className="font-medium text-gray-800">
                      {m.userId?.name || m.name}
                    </div>
                    <div className="text-xs text-gray-400">
                      {m.role || "—"} · added {fmtDate(m.addedAt)}
                      {m.addedBy?.name ? ` by ${m.addedBy.name}` : ""}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button className={btnGhost} onClick={() => editRole(m)}>
                      Role
                    </button>
                    <button className={btnGhost} onClick={() => remove(m)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            {!isFetching && activeMembers.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                No team members yet.
              </div>
            )}
          </div>
        </section>

        <section className={panelClass}>
          <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Add from staff
            </h2>
            <input
              className={`${fieldClass} ml-auto w-48`}
              placeholder="Search staff"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="max-h-[520px] divide-y divide-gray-100 overflow-y-auto">
            {candidates.map((s) => (
              <div key={s._id} className="flex items-center justify-between px-5 py-2.5">
                <div>
                  <div className="text-sm font-medium text-gray-800">{s.name}</div>
                  <div className="text-xs text-gray-400">
                    {s.role || "—"} · {s.phone || "—"}
                  </div>
                </div>
                <button className={btnPrimary} onClick={() => add(s)}>
                  Add
                </button>
              </div>
            ))}
            {candidates.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                No matching staff.
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default NcsTeam;
